import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";
import { getItem } from "../../../../utils/localStorage";

const EditPurchaseOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams(); 
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Form fields
  const [poNumber, setPoNumber] = useState("");
  const [prNumber, setPrNumber] = useState("");
  const [currency, setCurrency] = useState("KES");
  const [supplier, setSupplier] = useState(null);
  const [department, setDepartment] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [supplierNotes, setSupplierNotes] = useState("");
  const [budget, setBudget] = useState("");
  const [status, setStatus] = useState("pending approval");

  // Current user state
  const [currentUser, setCurrentUser] = useState(null);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");

  // Items array
  const [items, setItems] = useState([]);

  // Options data
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [budgetOptions, setBudgetOptions] = useState([]);
  const [unitOfMeasureOptions] = useState([
    { label: "Pieces", value: "Pieces" },
    { label: "Kg", value: "Kg" },
    { label: "Liters", value: "Liters" },
    { label: "Meters", value: "Meters" },
    { label: "Boxes", value: "Boxes" },
    { label: "Packs", value: "Packs" }
  ]);

  // Status options
  const statusOptions = [
    { label: "Pending Approval", value: "pending approval" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Sent", value: "sent" },
    { label: "Received", value: "received" },
    { label: "Completed", value: "completed" },
    { label: "Canceled", value: "canceled" }
  ];

  // Loading & submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPO, setIsLoadingPO] = useState(true);

  // Original PO data for comparison
  const [originalPOData, setOriginalPOData] = useState(null);

  // Fetch user data when component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser;

        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue;
        }

        if (parsedUser?.user) {
          const fullName = parsedUser.user.fullName;
          const role = parsedUser.user.role;

          setCurrentUser(parsedUser.user);
          setFullName(fullName);
          setRole(role);
        } else {
          console.error("Invalid user structure:", parsedUser);
        }
      } catch (err) {
        console.log(err.message);
      }
    };

    getCurrentUser();
  }, []);

  // Fetch purchase order data
  useEffect(() => {
    if (!orderId || !selectedFacilityId) return;

    const fetchPurchaseOrder = async () => {
      setIsLoadingPO(true);
      try {
        const response = await makeRequest2(
          `/api/app/purchase-orders/details/${selectedFacilityId}/${orderId}`,
          "GET"
        );

        if (response.success && response.data) {
          const poData = response.data;
          setOriginalPOData(poData);

          // Set form fields from fetched data
          setPoNumber(poData.poNumber || "");
          setPrNumber(poData.prNumber || "");
          setCurrency(poData.currency || "KES");
          setSupplier(poData.supplier || null);
          setDepartment(poData.department || "");
          setInternalNotes(poData.internalNotes || "");
          setSupplierNotes(poData.supplierNotes || "");
          setBudget(poData.budget || "");
          setStatus(poData.status || "pending approval");

          // Set items with proper mapping
          const mappedItems = poData.items?.map(item => ({
            id: item._id,
            description: item.itemDescription || "",
            quantity: item.quantity || 1,
            unitOfMeasure: item.unitOfMeasure || "Pieces",
            unitPrice: item.unitPrice || 0,
            taxRate: item.taxRate || 0,
            total: item.totalPrice || 0
          })) || [];
          
          setItems(mappedItems);
        } else {
          toastify("Failed to fetch purchase order", "error");
          navigate("/facility/procurement_management/purchase_orders");
        }
      } catch (error) {
        console.error("Error fetching purchase order:", error);
        toastify("Error fetching purchase order", "error");
        navigate("/facility/procurement_management/purchase_orders");
      } finally {
        setIsLoadingPO(false);
      }
    };

    fetchPurchaseOrder();
  }, [orderId, selectedFacilityId, navigate]);

  // Fetch dropdown options data
  useEffect(() => {
    if (!selectedFacilityId) return;

    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        // Fetch suppliers
        const suppliersResponse = await makeRequest2(
          `/api/app/suppliers/${selectedFacilityId}`,
          "GET"
        );
        if (suppliersResponse.success && Array.isArray(suppliersResponse.data)) {
          setSupplierOptions(suppliersResponse.data);
        }

        // Fetch departments
        const departmentsResponse = await makeRequest2(
          `/api/app/departments/${selectedFacilityId}`,
          "GET"
        );
        if (departmentsResponse.success && Array.isArray(departmentsResponse.data)) {
          setDepartmentOptions(departmentsResponse.data);
        }

        // Fetch budgets
        const budgetsResponse = await makeRequest2(
          `/api/app/budgets/${selectedFacilityId}`,
          "GET"
        );
        if (budgetsResponse.success && Array.isArray(budgetsResponse.data)) {
          setBudgetOptions(budgetsResponse.data);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toastify("Error fetching initial data", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [selectedFacilityId]);

  // If no facility is selected, display a message and exit early
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="text-center p-5">Please select a facility first.</div>
      </Layout>
    );
  }

  // Add a new item row
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        id: null, // New items don't have ID yet
        description: "",
        quantity: 1,
        unitOfMeasure: "Pieces",
        unitPrice: 0,
        taxRate: 0,
        total: 0,
      },
    ]);
  };

  // Calculate item total
  const calculateItemTotal = (unitPrice, quantity, taxRate) => {
    const subtotal = parseFloat(unitPrice || 0) * parseFloat(quantity || 0);
    const tax = subtotal * (parseFloat(taxRate || 0) / 100);
    return subtotal + tax;
  };

  // Handle changes for each item field
  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };

          // Recalculate total if price, quantity or tax rate changes
          if (field === "unitPrice" || field === "quantity" || field === "taxRate") {
            updatedItem.total = calculateItemTotal(
              field === "unitPrice" ? value : item.unitPrice,
              field === "quantity" ? value : item.quantity,
              field === "taxRate" ? value : item.taxRate
            );
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  // Remove an item row
  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = items.reduce(
      (sum, item) => sum + (parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0)),
      0
    );

    const taxTotal = items.reduce(
      (sum, item) => {
        const itemSubtotal = parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0);
        const itemTax = itemSubtotal * (parseFloat(item.taxRate || 0) / 100);
        return sum + itemTax;
      },
      0
    );

    const grandTotal = subtotal + taxTotal;

    return {
      subtotal: subtotal.toFixed(2),
      taxTotal: taxTotal.toFixed(2),
      grandTotal: grandTotal.toFixed(2)
    };
  };

  // Reset form to original values
  const handleReset = () => {
    if (originalPOData) {
      setPoNumber(originalPOData.poNumber || "");
      setPrNumber(originalPOData.prNumber || "");
      setCurrency(originalPOData.currency || "KES");
      setSupplier(originalPOData.supplier || null);
      setDepartment(originalPOData.department || "");
      setInternalNotes(originalPOData.internalNotes || "");
      setSupplierNotes(originalPOData.supplierNotes || "");
      setBudget(originalPOData.budget || "");
      setStatus(originalPOData.status || "pending approval");

      const mappedItems = originalPOData.items?.map(item => ({
        id: item._id,
        description: item.itemDescription || "",
        quantity: item.quantity || 1,
        unitOfMeasure: item.unitOfMeasure || "Pieces",
        unitPrice: item.unitPrice || 0,
        taxRate: item.taxRate || 0,
        total: item.totalPrice || 0
      })) || [];
      
      setItems(mappedItems);
    }
  };

  // Submit the updated purchase order
  const handleSubmit = async () => {
    if (items.length === 0) {
      toastify("Please add at least one item", "error");
      return;
    }

    if (!supplier) {
      toastify("Please select a supplier", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const totals = calculateTotals();

      const payload = {
        poNumber,
        prNumber,
        currency,
        supplierId: supplier,
        department,
        internalNotes,
        supplierNotes,
        budget,
        status,
        items: items.map((item) => ({
          _id: item.id, // Include ID for existing items
          itemDescription: item.description,
          quantity: item.quantity,
          unitOfMeasure: item.unitOfMeasure,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
          totalPrice: item.total
        })),
        subtotal: parseFloat(totals.subtotal),
        taxTotal: parseFloat(totals.taxTotal),
        grandTotal: parseFloat(totals.grandTotal)
      };

      const response = await makeRequest2(
        `/api/app/purchase-orders/${orderId}`,
        "PUT",
        payload
      );

      if (!response.success) {
        toastify(response.error || "Failed to update purchase order", "error");
        return;
      }

      toastify(response.message || "Purchase Order updated successfully", "success");
      navigate("/facility/procurement_management/purchase_orders");
    } catch (error) {
      toastify("An unexpected error occurred: " + (error.message || "Unknown error"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totals = calculateTotals();

  // Show loading spinner while fetching PO data
  if (isLoadingPO) {
    return (
      <Layout>
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading purchase order...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/purchase_orders">
                    Purchase Orders
                  </Link>
                </li>
                <li className="breadcrumb-item">Edit Purchase Order</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="/facility/procurement_management/purchase_orders">
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5>Edit Purchase Order - {poNumber}</h5>
              {fullName && (
                <div className="text-muted">
                  Modified by: {fullName} {role ? `(${role})` : ''}
                </div>
              )}
            </div>
            <div className="card-body">
              {/* Loading indicator for dropdown data */}
              {isLoading && (
                <div className="text-center mb-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading dropdown data...</p>
                </div>
              )}

              {/* Main Form Fields */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">PO Number:</label>
                    <InputText
                      value={poNumber}
                      onChange={(e) => setPoNumber(e.target.value)}
                      className="w-100"
                      disabled={isSubmitting}
                      readOnly // PO Number should typically not be editable
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">PR Number:</label>
                    <InputText
                      value={prNumber}
                      onChange={(e) => setPrNumber(e.target.value)}
                      className="w-100"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Currency:</label>
                    <InputText
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="w-100"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Status:</label>
                    <Dropdown
                      value={status}
                      options={statusOptions}
                      onChange={(e) => setStatus(e.value)}
                      className="w-100"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Supplier:</label>
                <Dropdown
                  value={supplier}
                  options={supplierOptions.map((sup) => ({
                    label: sup.name,
                    value: sup._id || sup.id,
                  }))}
                  onChange={(e) => setSupplier(e.value)}
                  placeholder="Search Supplier"
                  className="w-100"
                  disabled={isSubmitting || isLoading}
                />
                {originalPOData?.supplierInfo && (
                  <small className="text-muted">
                    Current: {originalPOData.supplierInfo.name}
                  </small>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Department:</label>
                <InputText
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  className="w-100"
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Budget:</label>
                <InputText
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="w-100"
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-4">
                <label className="form-label">Internal notes:</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-4">
                <label className="form-label">Supplier notes:</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={supplierNotes}
                  onChange={(e) => setSupplierNotes(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              {/* Items Section */}
              <h5>ITEM(S)</h5>
              <div className="mb-3">
                <button
                  className="btn btn-primary"
                  onClick={handleAddItem}
                  disabled={isSubmitting}>
                  Add Item
                </button>
              </div>

              {/* Items Table */}
              <div className="table-responsive mb-4">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Item Description <span className="text-danger">*</span></th>
                      <th>Quantity</th>
                      <th>Unit of Measure</th>
                      <th>Unit Price</th>
                      <th>Tax Rate</th>
                      <th>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.id || index}>
                        {/* Item Description */}
                        <td>
                          <textarea
                            className="form-control"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, "description", e.target.value)}
                            rows={2}
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Quantity */}
                        <td>
                          <InputNumber
                            value={item.quantity}
                            onValueChange={(e) => handleItemChange(index, "quantity", e.value)}
                            min={1}
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Unit of Measure */}
                        <td>
                          <Dropdown
                            value={item.unitOfMeasure}
                            options={unitOfMeasureOptions}
                            onChange={(e) => handleItemChange(index, "unitOfMeasure", e.value)}
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Unit Price */}
                        <td>
                          <InputNumber
                            value={item.unitPrice}
                            onValueChange={(e) => handleItemChange(index, "unitPrice", e.value)}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Tax Rate */}
                        <td>
                          <InputNumber
                            value={item.taxRate}
                            onValueChange={(e) => handleItemChange(index, "taxRate", e.value)}
                            suffix="%"
                            className="w-100"
                            disabled={isSubmitting}
                          />
                        </td>

                        {/* Total */}
                        <td>
                          {currency} {parseFloat(item.total || 0).toFixed(2)}
                        </td>

                        {/* Remove Button */}
                        <td className="text-center">
                          <Button
                            icon="ti ti-trash"
                            className="p-button-rounded p-button-danger mr-2"
                            onClick={() => handleRemoveItem(index)}
                            disabled={isSubmitting}
                          />
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No items added. Click "ADD ITEM" to begin.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="row mb-4 justify-content-end">
                <div className="col-md-6">
                  <div className="d-flex justify-content-between mb-2">
                    <div><strong>Subtotal:</strong></div>
                    <div><strong>{currency} {totals.subtotal}</strong></div>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <div><strong>Total Tax:</strong></div>
                    <div><strong>{currency} {totals.taxTotal}</strong></div>
                  </div>
                  <div className="d-flex justify-content-between border-top pt-2">
                    <div><strong>Grand Total:</strong></div>
                    <div><strong>{currency} {totals.grandTotal}</strong></div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="text-end mt-4">
                <Button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={handleReset}
                  disabled={isSubmitting}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  className="btn btn-success me-2"
                  onClick={handleSubmit}
                  disabled={isSubmitting || items.length === 0}
                >
                  {isSubmitting ? "Updating..." : "Update Purchase Order"}
                </Button>
                <Button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate("/facility/procurement_management/purchase_orders")}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditPurchaseOrder;